import {
  type MutableRefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import SidePanel from '~/shared/components/SidePanel/SidePanel';

import type { ExplorationFilter } from '../../types/exploration';
import { useCurrentAccountVolumes, useExplorationState } from '../../context/ExplorationContext';
import ExplorationFilterPanelHeader from './ExplorationFilterPanelHeader';
import FilterPanelContent from './FilterPanelContent';
import { useExplorationFilterState } from './hooks/useExplorationFilterState';
import {
  type CapacityBounds,
  hasPendingFilters,
} from './utils/filterConverters';

const explorationFilterToggleSelector = '[data-exploration-filter-toggle]';
export const explorationFiltersPanelId = 'exploration-filters-panel';

const explorationFilterPanelExcludeSelectors = [explorationFilterToggleSelector];

interface ExplorationFiltersSidePanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onResetFilters: () => void;
  onApplyFilters: (filters: ExplorationFilter[]) => void;
  appliedFilters: ExplorationFilter[];
  capacityBounds: CapacityBounds;
  closeWithApplyRef?: MutableRefObject<(() => void) | undefined>;
}

export default function ExplorationFiltersSidePanel({
  open,
  onOpenChange,
  onResetFilters,
  onApplyFilters,
  appliedFilters,
  capacityBounds,
  closeWithApplyRef,
}: ExplorationFiltersSidePanelProps) {
  const volumes = useCurrentAccountVolumes();
  const { view } = useExplorationState();
  const [filterSearchQuery, setFilterSearchQuery] = useState('');
  const filterState = useExplorationFilterState({
    open,
    appliedFilters,
    capacityBounds,
  });

  useEffect(() => {
    if (!open) {
      setFilterSearchQuery('');
    }
  }, [open]);

  const applyPendingFiltersRef = useRef(filterState.applyPendingFilters);
  applyPendingFiltersRef.current = filterState.applyPendingFilters;

  const handleClose = useCallback(() => {
    onApplyFilters(applyPendingFiltersRef.current());
  }, [onApplyFilters]);

  const handleCloseAndDismiss = useCallback(() => {
    handleClose();
    onOpenChange(false);
  }, [handleClose, onOpenChange]);

  useLayoutEffect(() => {
    if (!closeWithApplyRef) {
      return;
    }
    closeWithApplyRef.current = handleCloseAndDismiss;
    return () => {
      closeWithApplyRef.current = undefined;
    };
  }, [closeWithApplyRef, handleCloseAndDismiss]);

  const handleResetFilters = () => {
    filterState.resetPendingFilters();
    onResetFilters();
    onOpenChange(false);
  };

  const hasActiveFilters = hasPendingFilters(filterState.pending, capacityBounds);

  return (
    <SidePanel
      id={explorationFiltersPanelId}
      open={open}
      onOpenChange={onOpenChange}
      width="w-[445px]"
      excludeSelectors={explorationFilterPanelExcludeSelectors}
      onClose={handleClose}
    >
      <div className="flex flex-col h-full">
        <ExplorationFilterPanelHeader
          searchQuery={filterSearchQuery}
          onSearchChange={setFilterSearchQuery}
          onClearAll={handleResetFilters}
          hasActiveFilters={hasActiveFilters}
          onClose={handleCloseAndDismiss}
        />

        <FilterPanelContent
          view={view}
          panelOpen={open}
          filterSearchQuery={filterSearchQuery}
          volumes={volumes}
          capacityBounds={capacityBounds}
          pending={filterState.pending}
          setField={filterState.setField}
        />
      </div>
    </SidePanel>
  );
}
